// @ts-strict-ignore
import * as React from 'react';
import { AppComponentProps, ReleaseComponentProps } from '@/typescriptHelpers';
import Text from 'antd/lib/typography/Text';
import FullCalendar from '@fullcalendar/react';
import dayGridPlugin from '@fullcalendar/daygrid';
import timeGridPlugin from '@fullcalendar/timegrid';
import interactionPlugin from '@fullcalendar/interaction';
import listPlugin from '@fullcalendar/list';
import {
    AutomationRun,
    Integration,
    IntegrationCapability,
    IntegrationType,
    LifecycleEventType,
    ReleaseCalendarEventType,
    ReleaseCalendarViewType,
    ReleaseNav,
    ReleaseStatus,
} from '@/types';
import useSetLastViewedReleaseStep from '../hooks/useSetLastViewedReleaseStep';
import Layout from 'antd/lib/layout';
import { antGrey, runwayColors, titleStyle, tagStyle } from '../styles/globalStyles';
import Button from 'antd/lib/button';
import {
    CalendarOutlined,
    CheckCircleOutlined,
    LeftOutlined,
    PauseOutlined,
    RedoOutlined,
    RightOutlined,
} from '@ant-design/icons';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '@/reducers/reducers';
import {
    selectAuthCredentials,
    selectIntegrationForCapability,
    selectRelease,
    selectIsDemoInstance,
    selectIsWorkflowsSSRStepsEnabled,
    selectDemoApp,
    selectAppIntegrations,
    selectApp,
    selectAppSettingAutomations,
    selectWorkflowId,
    selectStepGraph,
} from '@/selectors/selectors';
import CreateEditReleaseModal from '../CreateEditReleaseModal';
import { PrepareNewWorkflowRunModal } from '../prepareRelease/PrepareNewWorkflowRunModal';
import Space from 'antd/lib/space';
import Flex from 'antd/lib/flex';
import PermissionedActionButton from '../HOCs/PermissionedActionButton';
import { RunwayUserAction } from '@/helpers/aclHelpers';
import { trySetAutomationPause } from '@/actions/app';
import { AppUpdatable } from '@/actions/updatable';
import { useError, useIsLoading, useUpdated } from '@/utils/hooks/helpers';
import { globalMessage } from '@/utils/globalMessage';
import Tooltip from 'antd/lib/tooltip';
import * as API from '../../api';
import {
    CalendarEventBaseData,
    capitalizeFirstLetter,
    getExternalEventsForReleaseCalendar,
    getLifecycleAndReleaseCycleEvents,
    ReleaseCalendarLifecycleFreezeEventData,
    ReleaseCalendarExternalEventData,
    ReleaseCalendarLifecycleEventData,
    ReleaseCalendarReleaseCycleEventData,
    sortCalendarEvents,
} from '@/helpers';
import { dayjs } from '../../extendedDayjs';
import ReleaseCyclePopover from './ReleaseCyclePopover';
import ReleaseCalendarEventPopover from './ReleaseCalendarEventPopover';
import { Divider } from 'antd/lib';
import Select from 'antd/lib/select';
import Spin from 'antd/lib/spin';
import { trackReleaseRouteEvent } from '@/actions/trackEvent';
import * as AnalyticEvents from '../../utils/analytics';
import { displayNameForIntegrationType, iconAndAltForIntegrationType } from '@/helpers/integrationHelpers';
import { getReleaseCalendarDemoData } from '../../helpers/demoHelpers';
import Popover from 'antd/lib/popover';
import Checkbox from 'antd/lib/checkbox';
import Form from 'antd/lib/form';
import { FilterOutlined } from '@ant-design/icons';
import { ReleaseCalendarLifecycleEvent } from './ReleaseCalendarLifecycleEvent';
import { getReleaseCalendarDotState, ReleaseCalendarDot } from './ReleaseCalendarDot';
import { ReleaseCalendarExternalEvent } from './ReleaseCalendarExternalEvent';
import { ReleaseStepIntegrationStatusBanner } from '../releaseSteps/ReleaseStepIntegrationStatusBanner';
import PauseAutomationsModal from '../PauseAutomationsModal';
import { ReleaseCalendarFilterBar, ReleaseCalendarFilterTab } from './ReleaseCalendarFilterBar';
import {
    bandsToMaskGradient,
    buildDestinationsViewLifecycleEvents,
    buildDestinationsViewReleaseCycleEvents,
    buildFlightpathsViewReleaseCycleEvents,
    buildStepConfigIdToArchetypeGroup,
    computeCyclingColorsByKey,
    computeStripedDayBands,
    isFullDayBand,
    mergeLifecycleEventsForFlightpaths,
    StripedDateRange,
} from './releaseCalendarGrouping';

const EmptyCalendarOverlay = () => (
    <div
        style={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            backgroundColor: antGrey.grey2,
            padding: '20px',
            borderRadius: '5px',
            textAlign: 'center',
            zIndex: 99999,
        }}
    >
        <Text style={{ margin: 100 }}>No release schedule dates found.</Text>
    </div>
);

const { Option } = Select;

const MAX_UNSELECTED_DESTINATION_ROWS = 5;
const RELEASE_BAR_CENTER_CHANGE_THRESHOLD_PX = 0.5;
const MIN_RELEASE_BAR_CELL_OVERLAP_PX = 1;
const MILLIS_PER_DAY = 24 * 60 * 60 * 1000;
const DAY_CELL_HORIZONTAL_PADDING_PX = 16;

type BackgroundHoverTarget =
    | {
          kind: 'freeze';
          id: string;
          event: ReleaseCalendarLifecycleFreezeEventData;
          startFraction: number;
          endFraction: number;
      }
    | {
          kind: 'blackout';
          id: string;
          event: ReleaseCalendarLifecycleEventData;
          startFraction: number;
          endFraction: number;
      };

const calcPercentWithPixelOffset = (percentage: number, offsetPx: number): string => {
    if (offsetPx === 0) {
        return `${percentage}%`;
    }
    const operator = offsetPx > 0 ? '+' : '-';
    return `calc(${percentage}% ${operator} ${Math.abs(offsetPx)}px)`;
};

const isReleaseCalendarElementHidden = (element: HTMLElement | null): boolean => {
    if (!element || typeof window === 'undefined') {
        return false;
    }

    const style = window.getComputedStyle(element);
    return style.display === 'none' || style.visibility === 'hidden';
};

const getReleaseBarSegmentDateKeys = (eventElement: HTMLElement, dayCell: HTMLElement): string[] => {
    const row = dayCell.closest('tr');
    const segmentDateKey = dayCell.getAttribute('data-date');
    if (!row || !segmentDateKey) {
        return segmentDateKey ? [segmentDateKey] : [];
    }

    const eventRect = eventElement.getBoundingClientRect();
    if (!Number.isFinite(eventRect.left) || eventRect.width === 0) {
        return [segmentDateKey];
    }

    return Array.from(row.querySelectorAll<HTMLElement>('.fc-daygrid-day[data-date]'))
        .filter((cell) => {
            const cellRect = cell.getBoundingClientRect();
            return (
                cellRect.right - eventRect.left > MIN_RELEASE_BAR_CELL_OVERLAP_PX &&
                eventRect.right - cellRect.left > MIN_RELEASE_BAR_CELL_OVERLAP_PX
            );
        })
        .map((cell) => cell.getAttribute('data-date'))
        .filter(Boolean) as string[];
};

const areReleaseBarCentersEqual = (
    a: Record<string, Record<string, number>>,
    b: Record<string, Record<string, number>>,
): boolean => {
    const aDateKeys = Object.keys(a);
    const bDateKeys = Object.keys(b);
    if (aDateKeys.length !== bDateKeys.length) {
        return false;
    }

    return aDateKeys.every((dateKey) => {
        const aGroupCenters = a[dateKey];
        const bGroupCenters = b[dateKey];
        if (!bGroupCenters) {
            return false;
        }

        const aGroupIds = Object.keys(aGroupCenters);
        const bGroupIds = Object.keys(bGroupCenters);
        return (
            aGroupIds.length === bGroupIds.length &&
            aGroupIds.every(
                (groupId) =>
                    bGroupCenters[groupId] != null &&
                    Math.abs(aGroupCenters[groupId] - bGroupCenters[groupId]) < RELEASE_BAR_CENTER_CHANGE_THRESHOLD_PX,
            )
        );
    });
};

interface ReleaseCalendarProps extends AppComponentProps {
    orgId: string;
    appId: string;
    lifecycleAutomations?: AutomationRun[];
    releaseId?: string;
    isAutomationPauseLoading?: boolean;
}

export const ReleaseCalendar: React.FC<ReleaseCalendarProps> = (props) => {
    const dispatch = useDispatch();
    const calendarRef = React.useRef(null);
    const calendarContainerRef = React.useRef<HTMLDivElement>(null);
    const dotHoveredReleaseBarRef = React.useRef<HTMLElement | null>(null);
    const pageTitle = 'Release Schedule';
    const [view, setView] = React.useState(ReleaseCalendarViewType.ReleaseCalendarViewTypeMonth);
    const [calendarLifecycleEvents, setCalendarLifecycleEvents] = React.useState<ReleaseCalendarLifecycleEventData[]>(
        [],
    );
    const [calendarReleaseCycleEvents, setCalendarReleaseCycleEvents] = React.useState<
        ReleaseCalendarReleaseCycleEventData[]
    >([]);
    const [calendarExternalEvents, setCalendarExternalEvents] = React.useState<ReleaseCalendarExternalEventData[]>([]);
    const [releaseLevelParentEvent, setReleaseLevelParentEvent] =
        React.useState<ReleaseCalendarReleaseCycleEventData | null>(null);
    const [hoveredReleaseVersion, setHoveredReleaseVersion] = React.useState<string | null>(null);
    const [filtersVisible, setFiltersVisible] = React.useState(false);
    const [hideSkippedReleases, setHideSkippedReleases] = React.useState(true);
    const [hideEvents, setHideEvents] = React.useState(false);
    const [releaseBarCenterByDateAndGroupId, setReleaseBarCenterByDateAndGroupId] = React.useState<
        Record<string, Record<string, number>>
    >({});
    const [form] = Form.useForm();
    const authCreds = useSelector((state: RootState) => selectAuthCredentials(state));
    const calendarIntegrationConnected = useSelector((state: RootState) =>
        selectIntegrationForCapability(state, props.appId, IntegrationCapability.Calendar),
    );
    const isDemoInstance = useSelector((state: RootState) => selectIsDemoInstance(state, props.orgId));
    const demoApp = useSelector((state: RootState) => selectDemoApp(state, props.appId));
    const release = useSelector((state: RootState) => selectRelease(state, props.releaseId));
    const isSSRMigrated = useSelector((state: RootState) => selectIsWorkflowsSSRStepsEnabled(state, props.appId));
    const workflowId = useSelector((state: RootState) => selectWorkflowId(state, props.appId));
    const stepGraph = useSelector((state: RootState) => selectStepGraph(state, props.appId));
    const [filterTab, setFilterTab] = React.useState(ReleaseCalendarFilterTab.Flightpaths);
    const [selectedDestinationIds, setSelectedDestinationIds] = React.useState<string[]>([]);
    const toggleDestinationSelected = React.useCallback((destinationId: string) => {
        setSelectedDestinationIds((current) =>
            current.includes(destinationId) ?
                current.filter((id) => id !== destinationId)
            :   [...current, destinationId],
        );
    }, []);
    const stepConfigIdToArchetypeGroup = React.useMemo(() => buildStepConfigIdToArchetypeGroup(stepGraph), [stepGraph]);
    const appIds = React.useMemo(() => [props.appId], [props.appId]);
    const flightpathColorByAppId = React.useMemo(
        () => (appIds.length > 1 ? computeCyclingColorsByKey(appIds) : {}),
        [appIds],
    );
    const visibleDestinationIds = React.useMemo(() => {
        if (selectedDestinationIds.length > 0) {
            return selectedDestinationIds;
        }
        const uniqueSortedDestinationIds = Array.from(
            new Set(calendarLifecycleEvents.map((event) => event.destinationId).filter(Boolean)),
        ).sort();
        return uniqueSortedDestinationIds.slice(0, MAX_UNSELECTED_DESTINATION_ROWS);
    }, [selectedDestinationIds, calendarLifecycleEvents]);
    const calendarIntegrationsInErrorState = useSelector((state: RootState) => {
        const appIntegrations = selectAppIntegrations(state, props.appId);
        return appIntegrations.filter(
            (integration) =>
                (integration.id === IntegrationType.GoogleCalendar ||
                    integration.id === IntegrationType.GooglePersonalCalendar) &&
                integration.integrationError,
        );
    });
    const [hasFetchedReleaseCalendar, setHasFetchedReleaseCalendar] = React.useState(false);
    const [initialDate, setInitialDate] = React.useState<Date | null>(null);
    const [showEmptyState, setShowEmptyState] = React.useState(false);
    const [fetchCalendarIsLoading, setFetchCalendarIsLoading] = React.useState(false);
    const [calendarLifecycleFreezeEvents, setCalendarLifecycleFreezeEvents] = React.useState<
        ReleaseCalendarLifecycleFreezeEventData[]
    >([]);
    const [pauseAutomationsModalVisible, setPauseAutomationsModalVisible] = React.useState(false);
    const [pendingResume, setPendingResume] = React.useState(false);

    const automationPauseSuccess = useUpdated(AppUpdatable.SetAutomationPause, props.appId);
    const automationPauseError = useError(AppUpdatable.SetAutomationPause, props.appId);

    const app = useSelector((state: RootState) => selectApp(state, props.appId));
    const automations = useSelector((state: RootState) => selectAppSettingAutomations(state, props.appId));
    const hasPausedAutomations = app?.allAutomationsPaused || (app?.pausedAutomationTypes?.length ?? 0) > 0;
    const pausedAutomationCount =
        app?.allAutomationsPaused ? (automations?.length ?? 0) : (app?.pausedAutomationTypes?.length ?? 0);

    React.useEffect(() => {
        if (automationPauseSuccess && pendingResume) {
            globalMessage.success('Automations resumed!');
            setPendingResume(false);
        }
    }, [automationPauseSuccess, pendingResume]);

    React.useEffect(() => {
        if (automationPauseError && pendingResume) {
            globalMessage.error(automationPauseError);
            setPendingResume(false);
        }
    }, [automationPauseError, pendingResume]);

    const stripedDayBands = React.useMemo(() => {
        const freezeRanges: StripedDateRange[] = calendarLifecycleFreezeEvents.map((event) => ({
            start: event.start,
            end: event.end,
        }));
        const blackoutRanges: StripedDateRange[] = calendarLifecycleEvents
            .filter((event) => event.eventType === LifecycleEventType.LifecycleEventTypeBlackoutPeriod)
            .map((event) => ({ start: event.start, end: event.end }));
        return computeStripedDayBands([...freezeRanges, ...blackoutRanges]);
    }, [calendarLifecycleFreezeEvents, calendarLifecycleEvents]);

    const backgroundHoverTargetsByDate = React.useMemo(() => {
        const targetsByDate: Record<string, BackgroundHoverTarget[]> = {};

        const addTarget = (
            kind: BackgroundHoverTarget['kind'],
            event: ReleaseCalendarLifecycleFreezeEventData | ReleaseCalendarLifecycleEventData,
        ) => {
            if (!event.start) {
                return;
            }

            const rangeStart = dayjs(event.start);
            const rangeEnd = event.end ? dayjs(event.end) : rangeStart.startOf('day').add(1, 'day');
            if (!rangeStart.isValid() || !rangeEnd.isValid() || !rangeEnd.isAfter(rangeStart)) {
                return;
            }

            let cursor = rangeStart.startOf('day');
            while (cursor.isBefore(rangeEnd)) {
                const dayStart = cursor;
                const dayEnd = cursor.add(1, 'day');
                const coverStart = rangeStart.isAfter(dayStart) ? rangeStart : dayStart;
                const coverEnd = rangeEnd.isBefore(dayEnd) ? rangeEnd : dayEnd;
                const startFraction = Math.max(0, coverStart.diff(dayStart) / MILLIS_PER_DAY);
                const endFraction = Math.min(1, coverEnd.diff(dayStart) / MILLIS_PER_DAY);

                if (endFraction > startFraction) {
                    const dateKey = dayStart.format('YYYY-MM-DD');
                    (targetsByDate[dateKey] ??= []).push({
                        kind,
                        id: `${kind}-${event.id}-${dateKey}`,
                        event,
                        startFraction,
                        endFraction,
                    } as BackgroundHoverTarget);
                }

                cursor = dayEnd;
            }
        };

        calendarLifecycleFreezeEvents.forEach((event) => addTarget('freeze', event));
        calendarLifecycleEvents
            .filter((event) => event.eventType === LifecycleEventType.LifecycleEventTypeBlackoutPeriod)
            .forEach((event) => addTarget('blackout', event));

        return targetsByDate;
    }, [calendarLifecycleFreezeEvents, calendarLifecycleEvents]);

    const displayedLifecycleEvents = React.useMemo(() => {
        if (!isSSRMigrated) {
            return calendarLifecycleEvents;
        }
        return filterTab === ReleaseCalendarFilterTab.Flightpaths ?
                mergeLifecycleEventsForFlightpaths(
                    calendarLifecycleEvents,
                    stepConfigIdToArchetypeGroup,
                    flightpathColorByAppId,
                )
            :   buildDestinationsViewLifecycleEvents(calendarLifecycleEvents, visibleDestinationIds);
    }, [
        isSSRMigrated,
        filterTab,
        calendarLifecycleEvents,
        stepConfigIdToArchetypeGroup,
        visibleDestinationIds,
        flightpathColorByAppId,
    ]);

    const displayedReleaseCycleEvents = React.useMemo(() => {
        if (!isSSRMigrated) {
            return calendarReleaseCycleEvents;
        }
        return filterTab === ReleaseCalendarFilterTab.Destinations ?
                buildDestinationsViewReleaseCycleEvents(
                    calendarLifecycleEvents,
                    calendarReleaseCycleEvents,
                    visibleDestinationIds,
                )
            :   buildFlightpathsViewReleaseCycleEvents(calendarReleaseCycleEvents, flightpathColorByAppId);
    }, [
        isSSRMigrated,
        filterTab,
        calendarLifecycleEvents,
        calendarReleaseCycleEvents,
        visibleDestinationIds,
        flightpathColorByAppId,
    ]);

    const { sortedCalendarLifecycleEvents, sortedCalendarReleaseCycleEvents } = React.useMemo(() => {
        if (!hoveredReleaseVersion && !hideSkippedReleases) {
            return {
                sortedCalendarLifecycleEvents: hideEvents ? [] : displayedLifecycleEvents,
                sortedCalendarReleaseCycleEvents: displayedReleaseCycleEvents,
            };
        }

        const { sortedLifecycleEvents, sortedReleaseCycleEvents } = sortCalendarEvents(
            displayedLifecycleEvents,
            displayedReleaseCycleEvents,
            calendarLifecycleFreezeEvents,
            hoveredReleaseVersion,
            hideSkippedReleases,
        );

        return {
            sortedCalendarLifecycleEvents: hideEvents ? [] : sortedLifecycleEvents,
            sortedCalendarReleaseCycleEvents: sortedReleaseCycleEvents,
        };
    }, [
        displayedLifecycleEvents,
        displayedReleaseCycleEvents,
        calendarLifecycleFreezeEvents,
        hoveredReleaseVersion,
        hideSkippedReleases,
        hideEvents,
    ]);

    const lifecycleBarEvents = React.useMemo(
        () =>
            sortedCalendarLifecycleEvents
                .filter((event) => event.eventType !== LifecycleEventType.LifecycleEventTypeBlackoutPeriod)
                .map((event) => ({ ...event, classNames: ['release-calendar-v2-lifecycle-fc-event'] })),
        [sortedCalendarLifecycleEvents],
    );

    const releaseBarEvents = React.useMemo(() => {
        const barEvents: any[] = [];

        sortedCalendarReleaseCycleEvents.forEach((event) => {
            const lifecycleEvents = displayedLifecycleEvents.filter((e) => e.groupId === event.groupId);
            const releaseStart = dayjs(event.start);
            const releaseEnd = dayjs(event.end);
            const isActual = (e: ReleaseCalendarLifecycleEventData) =>
                e.eventDateType === ReleaseCalendarEventType.ReleaseCalendarEventTypeActualDate;
            const isRolloutEvent = (e: ReleaseCalendarLifecycleEventData) =>
                e.eventType === LifecycleEventType.LifecycleEventTypeRolloutIncrement ||
                e.eventType === LifecycleEventType.LifecycleEventTypeRolloutCompletion;
            const latestOf = (times: dayjs.Dayjs[]) =>
                times.length ? times.reduce((a, b) => (b.isAfter(a) ? b : a)) : null;
            const releaseEventTime = latestOf(
                lifecycleEvents
                    .filter((e) => e.eventType === LifecycleEventType.LifecycleEventTypeRelease)
                    .map((e) => dayjs(e.start)),
            );
            const latestActualTime = latestOf(lifecycleEvents.filter(isActual).map((e) => dayjs(e.start)));
            const boundaryTime = latestOf([releaseEventTime, latestActualTime].filter(Boolean)) ?? releaseStart;
            const hasExpectedRolloutTail = lifecycleEvents.some(
                (e) => isRolloutEvent(e) && !isActual(e) && dayjs(e.start).isAfter(boundaryTime),
            );

            const base = {
                ...event,
                customOrder: -1,
                barFullId: event.id,
                barFullStart: event.start,
                barFullEnd: event.end,
            };
            const pushFull = () =>
                barEvents.push({
                    ...base,
                    classNames: [
                        'release-calendar-v2-release-bar',
                        'release-calendar-v2-bar-real-start',
                        'release-calendar-v2-bar-real-end',
                    ],
                });
            const pushReleaseProgressSegments = () => {
                const transitionDay = boundaryTime.startOf('day');
                const firstDay = releaseStart.startOf('day');
                const lastDay = releaseEnd.subtract(1, 'millisecond').startOf('day');

                if (transitionDay.isBefore(firstDay) || transitionDay.isAfter(lastDay)) {
                    pushFull();
                    return;
                }

                if (transitionDay.isAfter(firstDay)) {
                    barEvents.push({
                        ...base,
                        id: `${event.id}::solid`,
                        end: transitionDay.format(),
                        classNames: [
                            'release-calendar-v2-release-bar',
                            'release-calendar-v2-bar-real-start',
                            'release-calendar-v2-bar-boundary-end',
                        ],
                    });
                }

                barEvents.push({
                    ...base,
                    id: `${event.id}::transition`,
                    start: transitionDay.format(),
                    end: transitionDay.add(1, 'day').format(),
                    barTransitionFraction: Math.min(1, Math.max(0, boundaryTime.diff(transitionDay) / MILLIS_PER_DAY)),
                    classNames: [
                        'release-calendar-v2-release-bar',
                        'release-calendar-v2-bar-transition',
                        !transitionDay.isAfter(firstDay) && 'release-calendar-v2-bar-real-start',
                        !transitionDay.isBefore(lastDay) && 'release-calendar-v2-bar-real-end',
                    ].filter(Boolean),
                });

                if (transitionDay.isBefore(lastDay)) {
                    barEvents.push({
                        ...base,
                        id: `${event.id}::dotted`,
                        start: transitionDay.add(1, 'day').format(),
                        classNames: [
                            'release-calendar-v2-release-bar',
                            'release-calendar-v2-bar-boundary-start',
                            'release-calendar-v2-bar-real-end',
                            'release-calendar-v2-bar-dashed',
                        ],
                    });
                }
            };

            if (!hasExpectedRolloutTail) {
                pushFull();
                return;
            }

            pushReleaseProgressSegments();
        });

        return barEvents;
    }, [sortedCalendarReleaseCycleEvents, displayedLifecycleEvents]);

    const measureVisibleReleaseBarCenters = React.useCallback(() => {
        const calendarContainer = calendarContainerRef.current;
        if (!calendarContainer) {
            return;
        }

        const nextCenters: Record<string, Record<string, number>> = {};
        calendarContainer.querySelectorAll<HTMLElement>('.release-calendar-v2-release-bar').forEach((eventElement) => {
            const eventHarness = eventElement.closest('.fc-daygrid-event-harness') as HTMLElement | null;
            if (
                eventElement.closest('.fc-popover-body') ||
                isReleaseCalendarElementHidden(eventElement) ||
                isReleaseCalendarElementHidden(eventHarness)
            ) {
                return;
            }

            const dayCell = eventElement.closest('.fc-daygrid-day') as HTMLElement | null;
            const releaseBarFill = eventElement.querySelector<HTMLElement>('.release-calendar-v2-release-bar-fill');
            const groupId = releaseBarFill?.dataset.releaseCalendarGroupId;
            if (!dayCell || !releaseBarFill || !groupId) {
                return;
            }

            const dateKeys = getReleaseBarSegmentDateKeys(eventElement, dayCell);
            if (dateKeys.length === 0) {
                return;
            }

            const dayFrame = dayCell.querySelector<HTMLElement>('.fc-daygrid-day-frame');
            const dayNumber = dayFrame?.querySelector<HTMLElement>('.fc-daygrid-day-number');
            if (!dayNumber) {
                return;
            }

            const releaseBarRect = releaseBarFill.getBoundingClientRect();
            const dayNumberRect = dayNumber.getBoundingClientRect();
            const centerTop = releaseBarRect.top + releaseBarRect.height / 2 - dayNumberRect.top;
            if (!Number.isFinite(centerTop)) {
                return;
            }

            dateKeys.forEach((dateKey) => {
                (nextCenters[dateKey] ??= {})[groupId] = centerTop;
            });
        });

        setReleaseBarCenterByDateAndGroupId((current) =>
            areReleaseBarCentersEqual(current, nextCenters) ? current : nextCenters,
        );
    }, []);

    const measurementFramesRef = React.useRef<{ first?: number; second?: number }>({});
    const cancelScheduledMeasurement = React.useCallback(() => {
        const frames = measurementFramesRef.current;
        if (frames.first != null) {
            window.cancelAnimationFrame(frames.first);
            frames.first = undefined;
        }
        if (frames.second != null) {
            window.cancelAnimationFrame(frames.second);
            frames.second = undefined;
        }
    }, []);
    const scheduleMeasureReleaseBarCenters = React.useCallback(() => {
        cancelScheduledMeasurement();
        if (typeof window === 'undefined' || !window.requestAnimationFrame) {
            measureVisibleReleaseBarCenters();
            return;
        }
        measurementFramesRef.current.first = window.requestAnimationFrame(() => {
            measurementFramesRef.current.second = window.requestAnimationFrame(measureVisibleReleaseBarCenters);
        });
    }, [cancelScheduledMeasurement, measureVisibleReleaseBarCenters]);

    React.useLayoutEffect(() => {
        if (view === ReleaseCalendarViewType.ReleaseCalendarViewTypeList) {
            setReleaseBarCenterByDateAndGroupId((current) => (Object.keys(current).length === 0 ? current : {}));
            return () => {};
        }

        scheduleMeasureReleaseBarCenters();
        return cancelScheduledMeasurement;
    }, [scheduleMeasureReleaseBarCenters, cancelScheduledMeasurement, releaseBarEvents, view]);

    const handleDatesSet = React.useCallback(() => {
        if (view === ReleaseCalendarViewType.ReleaseCalendarViewTypeList) {
            return;
        }
        scheduleMeasureReleaseBarCenters();
    }, [scheduleMeasureReleaseBarCenters, view]);

    const lifecycleDotsByDate = React.useMemo(() => {
        const now = dayjs();
        const byDate: Record<
            string,
            {
                id: string;
                color: string;
                state: ReturnType<typeof getReleaseCalendarDotState>;
                fraction: number;
                groupId: string;
                overlapIndex: number;
                topOffset: number;
            }[]
        > = {};
        const overlapCountByDateTimeAndGroup: Record<string, number> = {};
        displayedLifecycleEvents.forEach((event) => {
            if (event.eventType === LifecycleEventType.LifecycleEventTypeBlackoutPeriod || !event.start) {
                return;
            }

            const eventTime = dayjs(event.start);
            const dateKey = eventTime.format('YYYY-MM-DD');
            const topOffset = releaseBarCenterByDateAndGroupId[dateKey]?.[event.groupId];
            if (topOffset == null) {
                return;
            }

            const timeKey = `${eventTime.format('YYYY-MM-DD HH:mm')}::${event.groupId}`;
            const overlapIndex = overlapCountByDateTimeAndGroup[timeKey] ?? 0;
            overlapCountByDateTimeAndGroup[timeKey] = overlapIndex + 1;
            (byDate[dateKey] ??= []).push({
                id: event.id,
                color: event.backgroundColor,
                state: getReleaseCalendarDotState(event, now),
                fraction: eventTime.diff(eventTime.startOf('day')) / (24 * 60 * 60 * 1000),
                groupId: event.groupId,
                overlapIndex,
                topOffset,
            });
        });
        return byDate;
    }, [displayedLifecycleEvents, releaseBarCenterByDateAndGroupId]);

    const dispatchReleaseBarHoverEvent = React.useCallback((element: HTMLElement, isHovering: boolean) => {
        if (typeof window === 'undefined') {
            return;
        }
        element.dispatchEvent(
            new MouseEvent(isHovering ? 'mouseover' : 'mouseout', {
                bubbles: true,
                cancelable: true,
                relatedTarget: null,
                view: window,
            }),
        );
    }, []);

    const findReleaseBarElementForDot = React.useCallback((dateKey: string, groupId: string): HTMLElement | null => {
        const container = calendarContainerRef.current;
        if (!container) {
            return null;
        }

        let matchingElement: HTMLElement | null = null;
        container.querySelectorAll<HTMLElement>('.release-calendar-v2-release-bar').forEach((eventElement) => {
            const eventHarness = eventElement.closest('.fc-daygrid-event-harness') as HTMLElement | null;
            if (
                matchingElement ||
                eventElement.closest('.fc-popover-body') ||
                isReleaseCalendarElementHidden(eventElement) ||
                isReleaseCalendarElementHidden(eventHarness)
            ) {
                return;
            }

            const dayCell = eventElement.closest('.fc-daygrid-day') as HTMLElement | null;
            const releaseBarFill = eventElement.querySelector<HTMLElement>('.release-calendar-v2-release-bar-fill');
            if (!dayCell || !releaseBarFill || releaseBarFill.dataset.releaseCalendarGroupId !== groupId) {
                return;
            }

            if (getReleaseBarSegmentDateKeys(eventElement, dayCell).includes(dateKey)) {
                matchingElement = releaseBarFill;
            }
        });

        return matchingElement;
    }, []);

    const handleDotHover = React.useCallback(
        (lifecycleEventId: string | null, groupId?: string, dateKey?: string) => {
            if (dotHoveredReleaseBarRef.current) {
                dispatchReleaseBarHoverEvent(dotHoveredReleaseBarRef.current, false);
                dotHoveredReleaseBarRef.current = null;
                setHoveredReleaseVersion(null);
            }

            const container = calendarContainerRef.current;
            if (!container) {
                return;
            }

            container
                .querySelectorAll('.release-calendar-v2-lifecycle-event-highlighted')
                .forEach((element) => element.classList.remove('release-calendar-v2-lifecycle-event-highlighted'));
            if (!lifecycleEventId) {
                return;
            }
            const escapedId =
                typeof CSS !== 'undefined' && CSS.escape ? CSS.escape(lifecycleEventId) : lifecycleEventId;
            container
                .querySelectorAll(`[data-lifecycle-pill-id="${escapedId}"]`)
                .forEach((element) => element.classList.add('release-calendar-v2-lifecycle-event-highlighted'));

            if (!groupId || !dateKey) {
                return;
            }

            const releaseBarElement = findReleaseBarElementForDot(dateKey, groupId);
            if (!releaseBarElement) {
                return;
            }

            dotHoveredReleaseBarRef.current = releaseBarElement;
            dispatchReleaseBarHoverEvent(releaseBarElement, true);
        },
        [dispatchReleaseBarHoverEvent, findReleaseBarElementForDot],
    );

    const handleChangeView = (value) => {
        if (calendarRef.current) {
            setView(value);
            calendarRef.current.getApi().changeView(value);
        }
    };

    const handlePrevClick = () => {
        if (calendarRef.current) {
            const calendarApi = calendarRef.current.getApi();
            calendarApi.prev();
        }
    };

    const handleNextClick = () => {
        if (calendarRef.current) {
            const calendarApi = calendarRef.current.getApi();
            calendarApi.next();
        }
    };

    const fetchReleaseCalendar = React.useCallback(() => {
        setFetchCalendarIsLoading(true);

        const fetchPromise =
            isSSRMigrated && workflowId ?
                API.getWFReleaseCalendar(
                    {
                        orgId: props.orgId,
                        workflowId,
                        releaseId: props.releaseId,
                    },
                    authCreds,
                )
            :   API.getReleaseCalendar(
                    {
                        orgId: props.orgId,
                        appId: props.appId,
                        releaseId: props.releaseId,
                    },
                    authCreds,
                );

        fetchPromise
            .then((response) => {
                const releaseCalendarData = response.data ? response.data.releaseCalendar : response.releaseCalendar;
                setHasFetchedReleaseCalendar(true);

                const [releaseCycleEvents, lifecycleEvents] = getLifecycleAndReleaseCycleEvents(releaseCalendarData);

                setCalendarLifecycleEvents(lifecycleEvents.map((event) => ({ ...event, appId: props.appId })));
                setCalendarReleaseCycleEvents(releaseCycleEvents.map((event) => ({ ...event, appId: props.appId })));
                setShowEmptyState(releaseCycleEvents.length === 0);

                let releaseCycleEvent = releaseCycleEvents.length === 1 ? releaseCycleEvents[0] : null;

                if (releaseCycleEvent) {
                    setReleaseLevelParentEvent(releaseCycleEvent);
                    setInitialDate(dayjs(releaseCycleEvent.start).toDate());
                } else {
                    setInitialDate(new Date());
                }

                if (response.externalEvents && !!calendarIntegrationConnected) {
                    const externalEvents = getExternalEventsForReleaseCalendar(
                        response.externalEvents,
                        calendarIntegrationConnected.id,
                        props.releaseId && releaseCycleEvent ? releaseCycleEvent : null,
                    );
                    setCalendarExternalEvents(externalEvents);
                }

                const lifecycleFreezes = response.data ? response.data.lifecycleFreezes : response.lifecycleFreezes;
                if (lifecycleFreezes?.length) {
                    const events: ReleaseCalendarLifecycleFreezeEventData[] = lifecycleFreezes.map(
                        ({ startDate, endDate, timezone }, index) => ({
                            id: `lifecycle-freeze-${index}`,
                            title: 'Lifecycle freeze',
                            allDay: true,
                            groupId: props.appId,
                            backgroundColor: antGrey.grey9,
                            start: startDate,
                            end: dayjs(endDate).add(1, 'days').startOf('day').toISOString(), // Need to add 1 day since the calendar always ends the range at midnight
                            timezone: timezone,
                            eventDateType: ReleaseCalendarEventType.ReleaseCalendarLifecycleFreezeEvent,
                        }),
                    );
                    setCalendarLifecycleFreezeEvents(events);
                }
            })
            .catch((error) => {
                globalMessage.error(error.message);
                setInitialDate(new Date());
                setShowEmptyState(true);
                return;
            })
            .finally(() => {
                setFetchCalendarIsLoading(false);
            });
    }, [authCreds, isSSRMigrated, workflowId]);

    const setReleaseCalendarDemoData = (
        lifecycleEvents: ReleaseCalendarLifecycleEventData[],
        releaseCycleEvents: ReleaseCalendarReleaseCycleEventData[],
    ) => {
        setCalendarLifecycleEvents(lifecycleEvents);
        setCalendarReleaseCycleEvents(releaseCycleEvents);
        setShowEmptyState(releaseCycleEvents.length === 0);
        if (releaseCycleEvents.length === 1) {
            const releaseCycleEvent = releaseCycleEvents[0];
            setReleaseLevelParentEvent(releaseCycleEvent);
            setInitialDate(dayjs(releaseCycleEvent.start).toDate());
        } else {
            setInitialDate(new Date());
        }
        setHasFetchedReleaseCalendar(true);
    };

    const hasStartedFetchRef = React.useRef(false);

    React.useEffect(() => {
        if (hasStartedFetchRef.current || hasFetchedReleaseCalendar) {
            return;
        }
        hasStartedFetchRef.current = true;

        if (isDemoInstance) {
            setFetchCalendarIsLoading(true);
            const [releaseCycleEvents, lifecycleEvents] = getReleaseCalendarDemoData(demoApp, release);
            setReleaseCalendarDemoData(
                lifecycleEvents as ReleaseCalendarLifecycleEventData[],
                releaseCycleEvents as ReleaseCalendarReleaseCycleEventData[],
            );
        } else {
            fetchReleaseCalendar();
        }
    }, []);

    React.useEffect(() => {
        return () => {
            setHoveredReleaseVersion(null);
        };
    }, []);

    React.useEffect(() => {
        if (!calendarContainerRef.current) {
            return;
        }
        const now = dayjs();
        const elapsedFraction = now.diff(now.startOf('day')) / (24 * 60 * 60 * 1000);
        calendarContainerRef.current.style.setProperty('--rw-day-elapsed', `${elapsedFraction * 100}%`);
    }, [initialDate, hasFetchedReleaseCalendar]);

    const handleTodayClick = () => {
        calendarRef.current?.getApi().today();
    };

    const [editRunwayReleaseSettingsModalVisible, setEditRunwayReleaseSettingsModalVisible] = React.useState(false);
    const buttonText =
        hasPausedAutomations ?
            `Resume ${pausedAutomationCount} ${pausedAutomationCount === 1 ? 'automation' : 'automations'}`
        :   'Pause automations...';
    const buttonIcon =
        hasPausedAutomations ?
            <RedoOutlined style={{ color: runwayColors.info }} />
        :   <PauseOutlined style={{ color: runwayColors.statusYellow }} />;

    const handleOnEditTargetDates = () => {
        fetchReleaseCalendar();
    };

    const updateAutomationPause = () => {
        if (hasPausedAutomations) {
            setPendingResume(true);
            dispatch(
                trySetAutomationPause(
                    {
                        orgId: props.orgId,
                        appId: props.appId,
                        enabled: true,
                        automationIds: app?.allAutomationsPaused ? undefined : app?.pausedAutomationTypes,
                    },
                    props.appId,
                ),
            );
        } else {
            setPauseAutomationsModalVisible(true);
        }
    };

    const handleReleaseCycleEventHover = (displayVersion: string) => {
        setHoveredReleaseVersion(displayVersion);
    };

    const handleReleaseCycleEventLeave = () => {
        setHoveredReleaseVersion(null);
    };

    const renderReleaseCycleEvent = (event: ReleaseCalendarReleaseCycleEventData) => {
        const lifecycleEvents = displayedLifecycleEvents.filter(
            (releaseCalendarEvent) => event.groupId === releaseCalendarEvent.groupId,
        );
        const isSkippedRelease = event.status === ReleaseStatus.Skipped;
        const isPredictedRelease = event.status === ReleaseStatus.Predicted;
        const title = isSkippedRelease ? `${event.title} (skipped)` : event.title;
        const barTransitionFraction = (event as any).barTransitionFraction;
        const releaseBarStart = dayjs(event.start).startOf('day');
        const releaseBarEnd = event.end ? dayjs(event.end) : releaseBarStart.add(1, 'day');
        const releaseBarDuration = releaseBarEnd.diff(releaseBarStart);
        const now = dayjs();
        const releaseBarDotOverlapCountByTime: Record<string, number> = {};
        const releaseBarDots: {
            id: string;
            color: string;
            state: ReturnType<typeof getReleaseCalendarDotState>;
            fraction: number;
            overlapIndex: number;
        }[] = [];
        lifecycleEvents.forEach((lifecycleEvent) => {
            if (
                lifecycleEvent.eventType === LifecycleEventType.LifecycleEventTypeBlackoutPeriod ||
                !lifecycleEvent.start
            ) {
                return;
            }

            const lifecycleEventTime = dayjs(lifecycleEvent.start);
            if (
                !releaseBarStart.isValid() ||
                !releaseBarEnd.isValid() ||
                releaseBarDuration <= 0 ||
                lifecycleEventTime.isBefore(releaseBarStart) ||
                !lifecycleEventTime.isBefore(releaseBarEnd)
            ) {
                return;
            }

            const timeKey = lifecycleEventTime.format('YYYY-MM-DD HH:mm');
            const overlapIndex = releaseBarDotOverlapCountByTime[timeKey] ?? 0;
            releaseBarDotOverlapCountByTime[timeKey] = overlapIndex + 1;

            releaseBarDots.push({
                id: lifecycleEvent.id,
                color: lifecycleEvent.backgroundColor,
                state: getReleaseCalendarDotState(lifecycleEvent, now),
                fraction: lifecycleEventTime.diff(releaseBarStart) / releaseBarDuration,
                overlapIndex,
            });
        });

        const eventContent =
            view !== ReleaseCalendarViewType.ReleaseCalendarViewTypeList ?
                <div
                    className={[
                        'release-calendar-v2-release-bar-fill',
                        barTransitionFraction != null && 'release-calendar-v2-release-bar-transition-fill',
                    ]
                        .filter(Boolean)
                        .join(' ')}
                    style={
                        {
                            '--rw-release-bar-color': event.parentEventColor,
                            backgroundColor: barTransitionFraction == null ? event.backgroundColor : undefined,
                            position: 'relative',
                            padding: '10px',
                            height: '24px',
                            marginTop: '12px',
                            boxSizing: 'border-box',
                        } as React.CSSProperties
                    }
                    data-release-calendar-group-id={event.groupId}
                    onMouseEnter={() => handleReleaseCycleEventHover(event.displayVersion)}
                    onMouseLeave={handleReleaseCycleEventLeave}
                >
                    <span
                        className={[
                            'release-calendar-v2-release-version',
                            isPredictedRelease && 'release-calendar-v2-release-version-predicted',
                        ]
                            .filter(Boolean)
                            .join(' ')}
                        style={{
                            backgroundColor: event.parentEventColor,
                            padding: '2px 4px',
                            position: 'absolute',
                            bottom: '100%',
                            left: '-1px',
                            zIndex: 1,
                            borderTopLeftRadius: '4px',
                            borderTopRightRadius: '4px',
                        }}
                    >
                        {title}
                    </span>
                    {barTransitionFraction != null && (
                        <>
                            <span
                                className="release-calendar-v2-release-bar-transition-solid"
                                style={{
                                    width: `${barTransitionFraction * 100}%`,
                                    backgroundColor: event.backgroundColor,
                                }}
                            />
                            <span
                                className="release-calendar-v2-release-bar-transition-dashed"
                                style={{
                                    left: `${barTransitionFraction * 100}%`,
                                    backgroundColor: event.backgroundColor,
                                }}
                            />
                        </>
                    )}
                    {releaseBarDots.length > 0 && (
                        <span className="release-calendar-v2-release-bar-dot-layer">
                            {releaseBarDots.map((dot) => (
                                <ReleaseCalendarDot
                                    key={dot.id}
                                    state={dot.state}
                                    color={dot.color}
                                    leftFraction={dot.fraction}
                                    overlapIndex={dot.overlapIndex}
                                    topOffset="50%"
                                />
                            ))}
                        </span>
                    )}
                </div>
            :   <Text
                    onMouseEnter={() => handleReleaseCycleEventHover(event.displayVersion)}
                    onMouseLeave={handleReleaseCycleEventLeave}
                >
                    {title}
                </Text>;

        return (
            <ReleaseCyclePopover
                orgId={props.orgId}
                appId={props.appId}
                releaseId={props.releaseId ?? props.appId + ((event as any).barFullId ?? event.id)}
                parentBackgroundColor={event.parentEventColor}
                backgroundColor={event.backgroundColor}
                eventContent={eventContent}
                isHotfix={event.isHotfix}
                displayVersion={event.title}
                calendarEvents={lifecycleEvents}
                releasePilotId={event.releasePilotId}
                releaseDescription={event.releaseDescription}
                lifecycleAutomations={props.lifecycleAutomations ?? null}
                start={(event as any).barFullStart ?? event.start}
                end={(event as any).barFullEnd ?? event.end}
                isSkippedRelease={isSkippedRelease}
            />
        );
    };

    const renderBackgroundHoverTarget = (target: BackgroundHoverTarget) => {
        const startFraction = Math.max(0, Math.min(1, target.startFraction));
        const endFraction = Math.max(startFraction, Math.min(1, target.endFraction));
        const widthFraction = endFraction - startFraction;
        const leftOffsetPx = 2 * DAY_CELL_HORIZONTAL_PADDING_PX * startFraction - DAY_CELL_HORIZONTAL_PADDING_PX;
        const widthOffsetPx = 2 * DAY_CELL_HORIZONTAL_PADDING_PX * widthFraction;
        const eventContent = (
            <span
                className="release-calendar-v2-background-hover-target"
                style={
                    {
                        left: calcPercentWithPixelOffset(startFraction * 100, leftOffsetPx),
                        width: calcPercentWithPixelOffset(widthFraction * 100, widthOffsetPx),
                    } as React.CSSProperties
                }
            />
        );

        if (target.kind === 'freeze') {
            const event = target.event;
            const end = event.end && dayjs(event.end).subtract(1, 'days').toISOString();
            return (
                <ReleaseCalendarEventPopover
                    key={target.id}
                    orgId={props.orgId}
                    appId={props.appId}
                    releaseId={props.releaseId ?? props.appId + event.id}
                    backgroundColor={event.backgroundColor}
                    eventContent={eventContent}
                    lifecycleAutomations={null}
                    eventDateType={ReleaseCalendarEventType.ReleaseCalendarLifecycleFreezeEvent}
                    eventType={LifecycleEventType.LifecycleEventTypeLifecycleFreeze}
                    isEventComplete={false}
                    eventDate={event.start}
                    isTargetDateScheduledFromCadence={false}
                    incrementDayNumber={0}
                    stagedRolloutPercentage={0}
                    isSkippedRelease={false}
                    title={event.title}
                    isExternalEvent={false}
                    eventEndDate={end}
                    timezone={event.timezone}
                    allDay={event.allDay}
                />
            );
        }

        const event = target.event;
        return (
            <ReleaseCalendarEventPopover
                key={target.id}
                orgId={props.orgId}
                appId={props.appId}
                releaseId={props.releaseId ?? props.appId + event.id}
                backgroundColor={event.backgroundColor}
                displayVersion={event.displayVersion}
                eventContent={eventContent}
                lifecycleAutomations={props.lifecycleAutomations ?? null}
                eventDateType={event.eventDateType}
                eventType={event.eventType}
                isEventComplete={event.isEventComplete}
                eventDate={event.start}
                isTargetDateScheduledFromCadence={event.targetDateSetByCadence}
                incrementDayNumber={event.incrementDayNumber}
                stagedRolloutPercentage={event.stagedRolloutPercentage}
                isSkippedRelease={event.status === ReleaseStatus.Skipped}
                isExternalEvent={false}
                eventEndDate={event.end}
                lifecycleEventsForDetails={[event]}
            />
        );
    };

    const renderLifecycleFreezeEvent = (event: ReleaseCalendarLifecycleFreezeEventData) => {
        const title = capitalizeFirstLetter(event.title);
        const end = event.end && dayjs(event.end).subtract(1, 'days').toISOString(); // Fixing date since we had to add 1 day earlier in the code
        const eventContent =
            view !== ReleaseCalendarViewType.ReleaseCalendarViewTypeList ?
                <div
                    style={{
                        backgroundColor: event.backgroundColor,
                        border: `1px solid ${event.backgroundColor}`,
                        opacity: 1,
                        position: 'relative',
                        padding: '10px',
                        height: '25px',
                        maxHeight: '25px',
                        boxSizing: 'border-box',
                    }}
                >
                    <span
                        style={{
                            backgroundColor: event.backgroundColor,
                            padding: '2px 4px',
                            position: 'absolute',
                            top: '0px',
                            left: '0px',
                            zIndex: 1,
                            height: '20px',
                            maxHeight: '20px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                        }}
                    >
                        {title}
                    </span>
                </div>
            :   <Text>{title}</Text>;
        return (
            <ReleaseCalendarEventPopover
                orgId={props.orgId}
                appId={props.appId}
                releaseId={props.releaseId ?? props.appId + event.id}
                backgroundColor={event.backgroundColor}
                eventContent={eventContent}
                lifecycleAutomations={null}
                eventDateType={ReleaseCalendarEventType.ReleaseCalendarLifecycleFreezeEvent}
                eventType={LifecycleEventType.LifecycleEventTypeLifecycleFreeze}
                isEventComplete={false}
                eventDate={event.start}
                isTargetDateScheduledFromCadence={false}
                incrementDayNumber={0}
                stagedRolloutPercentage={0}
                isSkippedRelease={false}
                title={event.title}
                isExternalEvent={false}
                eventEndDate={end}
                timezone={event.timezone}
                allDay={event.allDay}
            />
        );
    };

    const renderExternalEvent = (event: ReleaseCalendarExternalEventData) => {
        return (
            <ReleaseCalendarExternalEvent
                orgId={props.orgId}
                appId={props.appId}
                releaseId={props.releaseId ?? props.appId + event.id}
                event={event}
                view={view}
                integration={calendarIntegrationConnected}
            />
        );
    };
    const renderReleaseLifecycleEvent = (event: ReleaseCalendarLifecycleEventData) => {
        return (
            <ReleaseCalendarLifecycleEvent
                event={event}
                lifecycleAutomations={props.lifecycleAutomations}
                appId={props.appId}
                orgId={props.orgId}
                releaseId={props.releaseId}
                view={view}
            />
        );
    };

    const renderCalendarSyncIntegration = (integration: Integration | undefined) => {
        if (!integration || integration.integrationError) {
            return null;
        }
        const [icon, alt] = iconAndAltForIntegrationType(integration.id);
        const calendarTooltipDesc = displayNameForIntegrationType(integration.id) + ' is connected and synced';
        return (
            <Tooltip
                title={calendarTooltipDesc}
                style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                }}
                placement="bottom"
            >
                <Space style={{ gap: '12px' }}>
                    <img src={icon} alt={alt} style={{ width: '16px', height: '16px', marginBottom: '3px' }} />
                    <CheckCircleOutlined
                        style={{
                            color: runwayColors.statusGreen,
                            width: '16px',
                            height: '16px',
                        }}
                    />
                </Space>
                <Divider
                    type="vertical"
                    style={{
                        height: '20px',
                        width: '5px',
                        borderColor: antGrey.grey5,
                        marginLeft: 12,
                        marginRight: 12,
                    }}
                />
            </Tooltip>
        );
    };

    const renderEventContent = (eventInfo: any) => {
        const event = eventInfo.event;
        const eventExtendedProps = eventInfo.event.extendedProps;

        const isParentEvent =
            eventExtendedProps.eventDateType === ReleaseCalendarEventType.ReleaseCalendarEventTypeParent;
        const isExternalEvent =
            eventExtendedProps.eventDateType == ReleaseCalendarEventType.ReleaseCalendarExternalEvent;
        const isFreezeEvent =
            eventExtendedProps.eventDateType == ReleaseCalendarEventType.ReleaseCalendarLifecycleFreezeEvent;

        const baseCalendarEvent: CalendarEventBaseData = {
            id: event.id,
            groupId: event.groupId,
            title: event.title,
            allDay: event.allDay,
            start: event.start,
            end: event.end,
            backgroundColor: event.backgroundColor,
        };

        if (isParentEvent) {
            return renderReleaseCycleEvent({
                ...baseCalendarEvent,
                ...eventExtendedProps,
            });
        }

        if (isExternalEvent) {
            return renderExternalEvent({
                ...baseCalendarEvent,
                ...eventExtendedProps,
            });
        }

        if (isFreezeEvent) {
            return renderLifecycleFreezeEvent({
                ...baseCalendarEvent,
                ...eventExtendedProps,
            });
        }

        return renderReleaseLifecycleEvent({
            ...baseCalendarEvent,
            ...eventExtendedProps,
        });
    };

    const customLocale = {
        code: 'custom',
        allDayText: view === ReleaseCalendarViewType.ReleaseCalendarViewTypeList ? 'Release' : '',
    };

    const handleDayCellClassNames = (arg: { date: Date }) => {
        const classNames: string[] = [];
        if (dayjs(arg.date).isSame(dayjs(), 'day')) {
            classNames.push('release-calendar-today-highlight');
        }
        if (stripedDayBands[dayjs(arg.date).format('YYYY-MM-DD')]) {
            classNames.push('release-calendar-v2-blackout');
        }
        return classNames;
    };

    const handleDayCellDidMount = (arg: { date: Date; el: HTMLElement }) => {
        const dateKey = dayjs(arg.date).format('YYYY-MM-DD');
        const bands = stripedDayBands[dateKey];
        if (bands && !isFullDayBand(bands)) {
            arg.el.style.setProperty('--rw-blackout-mask', bandsToMaskGradient(bands));
        }
    };

    const applyFilters = (values: { hideEvents: boolean; hideSkippedReleases: boolean }) => {
        setHideEvents(values.hideEvents);
        setHideSkippedReleases(values.hideSkippedReleases);
        setFiltersVisible(false);
    };

    const clearButtonClicked = () => {
        setHideEvents(false);
        setHideSkippedReleases(false);
        form.setFieldsValue({ hideEvents: false, hideSkippedReleases: false });
    };

    const renderFilterModalContent = () => (
        <div style={{ minWidth: 200 }}>
            <div style={{ padding: 12 }}>
                <Form
                    form={form}
                    initialValues={{ hideEvents, hideSkippedReleases }}
                    onFinish={applyFilters}
                    preserve={false}
                    layout="vertical"
                >
                    <Flex vertical gap={12}>
                        <Form.Item name="hideEvents" valuePropName="checked" noStyle>
                            <Checkbox>Hide events</Checkbox>
                        </Form.Item>
                        <Form.Item name="hideSkippedReleases" valuePropName="checked" noStyle>
                            <Checkbox>Hide skipped releases</Checkbox>
                        </Form.Item>
                    </Flex>
                </Form>
            </div>
            <div
                style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    padding: 12,
                    backgroundColor: antGrey.grey3,
                }}
            >
                <Button size="small" onClick={clearButtonClicked}>
                    Clear
                </Button>
                <Button size="small" onClick={form.submit} type="primary">
                    Apply
                </Button>
            </div>
        </div>
    );

    return (
        <Layout
            style={{
                backgroundColor: runwayColors.white,
                position: 'relative',
            }}
        >
            <ReleaseStepIntegrationStatusBanner
                orgId={props.orgId}
                appId={props.appId}
                integrations={calendarIntegrationsInErrorState}
            />
            <div
                style={{
                    overflow: 'auto',
                    display: 'flex',
                    flexDirection: 'column',
                }}
            >
                <div
                    style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        padding: 24,
                        backgroundColor: antGrey.grey3,
                    }}
                >
                    <div>
                        <Text style={{ ...titleStyle, marginRight: 8 }} type="secondary">
                            {pageTitle}
                        </Text>
                        <Button type="link" size="small" style={{ color: antGrey.grey7 }} onClick={handlePrevClick}>
                            {<LeftOutlined />}
                        </Button>
                        <Button type="link" size="small" style={{ color: antGrey.grey7 }} onClick={handleNextClick}>
                            {<RightOutlined />}
                        </Button>
                        <Button
                            type="default"
                            size="small"
                            style={{ color: antGrey.grey7, marginLeft: 10 }}
                            onClick={handleTodayClick}
                        >
                            Today
                        </Button>
                    </div>
                    <div style={{ display: 'flex' }}>
                        <div>
                            {props.releaseId && releaseLevelParentEvent && (
                                <PermissionedActionButton
                                    icon={<CalendarOutlined style={{ color: runwayColors.info }} />}
                                    onClick={() => setEditRunwayReleaseSettingsModalVisible(true)}
                                    disabled={releaseLevelParentEvent && releaseLevelParentEvent.isReleaseCompleted}
                                    type="link"
                                >
                                    Edit target dates
                                </PermissionedActionButton>
                            )}

                            {props.releaseId && (
                                <>
                                    <PermissionedActionButton
                                        actionType={RunwayUserAction.UAPauseResumeScheduleCadence}
                                        releaseId={props.releaseId}
                                        icon={buttonIcon}
                                        onClick={updateAutomationPause}
                                        loading={props.isAutomationPauseLoading}
                                    >
                                        {buttonText}
                                    </PermissionedActionButton>

                                    <Divider
                                        type="vertical"
                                        style={{
                                            height: '20px',
                                            width: '5px',
                                            borderColor: antGrey.grey5,
                                            marginLeft: 12,
                                            marginRight: 12,
                                        }}
                                    />
                                </>
                            )}
                            {!props.releaseId && (
                                <>
                                    <Popover
                                        overlayClassName="filters-overlay"
                                        destroyTooltipOnHide
                                        content={renderFilterModalContent()}
                                        title={
                                            <Text
                                                style={{ ...tagStyle, fontSize: 12, color: antGrey.grey7, padding: 15 }}
                                            >
                                                Filters
                                            </Text>
                                        }
                                        trigger="click"
                                        open={filtersVisible}
                                        placement="left"
                                        onOpenChange={(open) => setFiltersVisible(open)}
                                    >
                                        <Button
                                            icon={<FilterOutlined style={{ color: runwayColors.primary }} />}
                                            type="default"
                                        >
                                            Filter
                                        </Button>
                                    </Popover>
                                    <Divider
                                        type="vertical"
                                        style={{
                                            height: '20px',
                                            width: '5px',
                                            borderColor: antGrey.grey5,
                                            marginLeft: 12,
                                            marginRight: 12,
                                        }}
                                    />
                                </>
                            )}
                            {renderCalendarSyncIntegration(calendarIntegrationConnected)}

                            <Select
                                defaultValue={ReleaseCalendarViewType.ReleaseCalendarViewTypeMonth}
                                style={{ width: 90 }}
                                onChange={handleChangeView}
                            >
                                <Option value={ReleaseCalendarViewType.ReleaseCalendarViewTypeMonth}>Month</Option>
                                <Option value={ReleaseCalendarViewType.ReleaseCalendarViewTypeWeek}>Week</Option>
                                <Option value={ReleaseCalendarViewType.ReleaseCalendarViewTypeDay}>Day</Option>
                                <Option value={ReleaseCalendarViewType.ReleaseCalendarViewTypeList}>List</Option>
                            </Select>
                        </div>
                    </div>
                </div>
                <div
                    style={{
                        padding: '18px 18px 95px 18px',
                    }}
                >
                    {isSSRMigrated && hasFetchedReleaseCalendar && (
                        <ReleaseCalendarFilterBar
                            appIds={appIds}
                            activeTab={filterTab}
                            onTabChange={setFilterTab}
                            selectedDestinationIds={selectedDestinationIds}
                            onToggleDestinationSelected={toggleDestinationSelected}
                        />
                    )}
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '32px' }}>
                        <div>
                            {fetchCalendarIsLoading && !hasFetchedReleaseCalendar && (
                                <div
                                    style={{
                                        display: 'flex',
                                        flexDirection: 'column',
                                        justifyContent: 'center',
                                        width: '100%',
                                        height: '100vh',
                                        flexGrow: 1,
                                    }}
                                >
                                    <Spin />
                                </div>
                            )}
                            {initialDate && (
                                <div
                                    ref={calendarContainerRef}
                                    className="release-calendar-v2"
                                    style={
                                        {
                                            position: 'relative',
                                            opacity: showEmptyState ? '50%' : null,
                                        } as React.CSSProperties
                                    }
                                >
                                    <FullCalendar
                                        ref={calendarRef}
                                        initialDate={showEmptyState ? null : initialDate}
                                        timeZone="local"
                                        height="auto"
                                        plugins={[dayGridPlugin, timeGridPlugin, interactionPlugin, listPlugin]}
                                        initialView={view}
                                        events={
                                            showEmptyState ? null
                                            : calendarExternalEvents ?
                                                [
                                                    ...releaseBarEvents,
                                                    ...lifecycleBarEvents,
                                                    ...calendarExternalEvents.map((event) => ({
                                                        ...event,
                                                        customOrder: 999,
                                                    })),
                                                ]
                                            :   [...releaseBarEvents, ...lifecycleBarEvents]
                                        }
                                        eventContent={renderEventContent}
                                        datesSet={handleDatesSet}
                                        headerToolbar={false}
                                        dayCellContent={(arg) => {
                                            const dateKey = dayjs(arg.date).format('YYYY-MM-DD');
                                            const isMonthView =
                                                view === ReleaseCalendarViewType.ReleaseCalendarViewTypeMonth;
                                            const dots = isMonthView ? lifecycleDotsByDate[dateKey] : undefined;
                                            const backgroundHoverTargets =
                                                isMonthView ? backgroundHoverTargetsByDate[dateKey] : undefined;
                                            return (
                                                <>
                                                    <span className="release-calendar-v2-day-top-content">
                                                        <span className="release-calendar-v2-day-number">
                                                            {arg.dayNumberText}
                                                        </span>
                                                        {arg.date.getDate() === 1 && (
                                                            <span className="release-calendar-v2-month-label">
                                                                {dayjs(arg.date).format('MMM').toUpperCase()}
                                                            </span>
                                                        )}
                                                    </span>
                                                    {dots?.length > 0 && (
                                                        <span className="release-calendar-v2-dot-layer">
                                                            {dots.map((dot) => (
                                                                <ReleaseCalendarDot
                                                                    key={dot.id}
                                                                    state={dot.state}
                                                                    color={dot.color}
                                                                    leftFraction={dot.fraction}
                                                                    overlapIndex={dot.overlapIndex}
                                                                    topOffset={dot.topOffset}
                                                                    onMouseEnter={() =>
                                                                        handleDotHover(dot.id, dot.groupId, dateKey)
                                                                    }
                                                                    onMouseLeave={() => handleDotHover(null)}
                                                                />
                                                            ))}
                                                        </span>
                                                    )}
                                                    {backgroundHoverTargets?.map(renderBackgroundHoverTarget)}
                                                </>
                                            );
                                        }}
                                        dayHeaders={true}
                                        dayHeaderFormat={{ weekday: 'long' }}
                                        locales={[customLocale]}
                                        locale="custom"
                                        dayCellClassNames={handleDayCellClassNames}
                                        dayCellDidMount={handleDayCellDidMount}
                                        dayMaxEvents={3}
                                        eventOrder="customOrder,start,-duration,allDay,title"
                                        eventDidMount={(info) => {
                                            if (info.el.closest('.fc-timeGridDay-view')) {
                                                info.el.style.paddingTop = '10px';
                                            }
                                            if (info.el.closest('.fc-popover-body')) {
                                                info.el.style.marginLeft = '-1px';
                                                info.el.style.maxWidth = '250px';
                                            }
                                        }}
                                    />
                                </div>
                            )}
                            {showEmptyState && <EmptyCalendarOverlay />}
                        </div>
                    </div>
                    {props.releaseId &&
                        releaseLevelParentEvent &&
                        (isSSRMigrated ?
                            <PrepareNewWorkflowRunModal
                                orgId={props.orgId}
                                appId={props.appId}
                                open={editRunwayReleaseSettingsModalVisible}
                                mode="standard"
                                isEditingRelease={true}
                                releaseId={props.releaseId}
                                displayOnlyTargetDates={true}
                                canBeSubmitted={releaseLevelParentEvent.canReleaseBeSubmitted}
                                onCancel={() => {
                                    setEditRunwayReleaseSettingsModalVisible(false);
                                }}
                                onSuccess={() => {
                                    setEditRunwayReleaseSettingsModalVisible(false);
                                }}
                                onEditTargetDates={handleOnEditTargetDates}
                            />
                        :   <CreateEditReleaseModal
                                orgId={props.orgId}
                                appId={props.appId}
                                open={editRunwayReleaseSettingsModalVisible}
                                onDidCreateOrEditRelease={() => {
                                    setEditRunwayReleaseSettingsModalVisible(false);
                                }}
                                onCancel={() => {
                                    setEditRunwayReleaseSettingsModalVisible(false);
                                }}
                                isEditingRelease={true}
                                releaseId={props.releaseId}
                                displayOnlyTargetDates={true}
                                canBeSubmitted={releaseLevelParentEvent.canReleaseBeSubmitted}
                                onEditTargetDates={handleOnEditTargetDates}
                            />)}
                    <PauseAutomationsModal
                        orgId={props.orgId}
                        appId={props.appId}
                        open={pauseAutomationsModalVisible}
                        onCancel={() => setPauseAutomationsModalVisible(false)}
                        onSuccess={() => setPauseAutomationsModalVisible(false)}
                    />
                </div>
            </div>
        </Layout>
    );
};

const ReleaseCalendarContainer: React.FC<ReleaseComponentProps> = (props: ReleaseComponentProps) => {
    const dispatch = useDispatch();

    useSetLastViewedReleaseStep(props.releaseId, ReleaseNav.ReleaseCalendar);
    React.useEffect(() => {
        dispatch(
            trackReleaseRouteEvent({
                orgId: props.orgId,
                appId: props.appId,
                releaseId: props.releaseId,
                eventName: AnalyticEvents.Event.ViewReleaseCalendar,
            }),
        );
    }, []);

    const lifecycleAutomations = useSelector(
        (state: RootState) => selectRelease(state, props.releaseId).lifecycleAutomations,
    );

    const resumeAutomationPauseIsLoading = useIsLoading(AppUpdatable.SetAutomationPause, props.appId);

    React.useEffect(() => {
        document.title = 'Release Schedule';
    }, []);

    return (
        <ReleaseCalendar
            orgId={props.orgId}
            appId={props.appId}
            releaseId={props.releaseId}
            lifecycleAutomations={lifecycleAutomations}
            isAutomationPauseLoading={resumeAutomationPauseIsLoading}
        />
    );
};

export default ReleaseCalendarContainer;
